import { z } from 'zod'

// The flight-log sync outbox (intra.flight_log_outbox): what the member app
// shows on a flight and what the admin outbox monitor lists. The machinery
// itself is server-only (apps/api/src/services/flightSync).

export const FLIGHT_SYNC_OPERATIONS = ['create', 'update', 'delete'] as const
export type FlightSyncOperation = (typeof FLIGHT_SYNC_OPERATIONS)[number]

// See db/flyway/schema/sql/V730__flight_log_outbox.sql for what each means.
export const OUTBOX_STATUSES = ['pending', 'sending', 'sent', 'failed', 'skipped'] as const
export type OutboxStatus = (typeof OUTBOX_STATUSES)[number]
export const OutboxStatusSchema = z.enum(OUTBOX_STATUSES)

// What a member sees on a flight: the status of its newest outbox row, in
// the words of the UI. `null` (no badge) when the flight never went through
// the outbox -- logged before the sync was switched on.
export const FLIGHT_SYNC_STATUSES = ['queued', 'synced', 'failed', 'skipped'] as const
export type FlightSyncStatus = (typeof FLIGHT_SYNC_STATUSES)[number]

export function flightSyncStatusOf(status: OutboxStatus): FlightSyncStatus {
  switch (status) {
    case 'pending':
    case 'sending':
      return 'queued'
    case 'sent':
      return 'synced'
    case 'failed':
      return 'failed'
    case 'skipped':
      return 'skipped'
  }
}

// How far back the admin monitor looks.
export const OUTBOX_WINDOWS = ['5m', '15m', '1h', '1d', '7d', '30d', 'all'] as const
export type OutboxWindow = (typeof OUTBOX_WINDOWS)[number]
export const OUTBOX_WINDOW_MINUTES: Record<Exclude<OutboxWindow, 'all'>, number> = {
  '5m': 5,
  '15m': 15,
  '1h': 60,
  '1d': 24 * 60,
  '7d': 7 * 24 * 60,
  '30d': 30 * 24 * 60,
}

export const OUTBOX_PAGE_SIZES = [25, 50, 100] as const

export const OutboxQuerySchema = z.object({
  status: OutboxStatusSchema.optional(),
  window: z.enum(OUTBOX_WINDOWS).default('1d'),
  page: z.coerce.number().int().min(1).max(10_000).default(1),
  pageSize: z.coerce
    .number()
    .int()
    .refine((n) => (OUTBOX_PAGE_SIZES as readonly number[]).includes(n))
    .default(25),
})
export type OutboxQuery = z.infer<typeof OutboxQuerySchema>

// One person on a flight, with the id they have in laskukierros
// (member.user_external_ids, system 'laskukierros'). `laskukierrosId` is
// null when the member has no mapping yet.
export interface FlightSyncPerson {
  userId: string
  displayName: string
  laskukierrosId: string | null
}

// The JSON an outbox row carries: everything needed to make the laskukierros
// entry, snapshotted when the flight was logged, corrected or deleted.
//
// The flight is entered under the BILLABLE member's logbook: the pilot, or --
// on a school flight, where the instructor is the pilot of record -- the
// student. `crew` is the one other person laskukierros records, an
// instructor or a co-pilot.
//
// Times in `flight` are the club's local wall clock labelled `Z`, because
// that is what laskukierros's own form sends; `instants` are the real UTC
// instants for anyone reading the row.
export interface FlightSyncPayload {
  version: 1
  operation: FlightSyncOperation
  flightId: string
  // The flight's id in laskukierros when it is already known (update and
  // delete); the worker looks it up again at send time when it is not.
  laskukierrosFlightId: number | null
  billableMember: FlightSyncPerson
  // The billable member's role in laskukierros.
  role: 'PIC' | 'copilot' | 'instructor' | 'student'
  crew: (FlightSyncPerson & { role: 'instructor' | 'copilot' }) | null
  timeZone: string
  flight: {
    registration: string
    flightType: string
    date: string
    departure: string
    arrival: string
    blockStart: string
    takeoff: string
    landing: string
    blockStop: string
    landingsDay: number
    landingsNight: number
    nightMinutes: number
    ifrMinutes: number
    comments: string
  }
  instants: { offBlockAt: string; takeoffAt: string; landingAt: string; onBlockAt: string }
}

// One row of the admin monitor.
export interface OutboxEntry {
  outboxId: string
  flightId: string
  operation: FlightSyncOperation
  status: OutboxStatus
  attempts: number
  lastError: string | null
  externalId: number | null
  nextAttemptAt: string
  processedAt: string | null
  createdAt: string
  createdBy: string | null
  createdByName: string | null
  // Enough to recognise the flight without opening the payload.
  registration: string | null
  billableName: string | null
  // The full JSON, shown formatted when the row is selected.
  payload: FlightSyncPayload
}

export interface OutboxPage {
  entries: OutboxEntry[]
  page: number
  pages: number
  total: number
  // Row counts per status over the same window (ignoring the status filter),
  // for the summary chips.
  counts: Record<OutboxStatus, number>
}

// What the monitor's header shows: is the worker on, how often, and what
// would it do.
export interface OutboxSettings {
  enabled: boolean
  intervalMinutes: number
  // Whether laskukierros credentials are set; without them rows stay queued.
  laskukierrosConfigured: boolean
  // True outside production: the worker validates each flight against the
  // live site but writes nothing (rows end 'skipped').
  dryRun: boolean
}
